import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS, DONUT_COLORS, DONUT_OTHER_COLOR, donutColor } from '../typings/chart';
import { ChartPointModel }                                           from '../typings/intelligence.chart-point';
import { ChartSeriesModel }                                          from '../typings/intelligence.chart-series';
import { DonutSegmentModel }                                         from '../typings/intelligence.donut-segment';
import { IntelligenceCustomerFiltersModel }                          from '../typings/intelligence.customer-filters';
import { IntelligenceCustomersModel }                                from '../typings/intelligence.customers';
import { IntelligenceFormatService }                                 from '../intelligence.format.service';
import { IntelligenceService }                                       from '../intelligence.service';
import { IntelligenceTopCustomerModel }                              from '../typings/intelligence.top-customer';
import { TableSortModel }                                            from '../typings/intelligence.table-sort';

@Component({
  selector   : 'wor-intelligence-customers-tab',
  template: require('./intelligence.customers-tab.component.html')
})
export class CustomersTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public filterOptions         : { customers : Array<string>; sources : Array<string>; suburbs : Array<string> } = null;
  public filters               : IntelligenceCustomerFiltersModel = { customer: '', customer_type: '', job_type: '', source: '', suburb: '' };
  public invoiceSourceSegments : Array<DonutSegmentModel> = [];
  public jobMixSegments        : Array<DonutSegmentModel> = [];
  public newVsReturningPoints  : Array<ChartPointModel> = [];
  public retentionValue        : string = '';
  public sort                  : TableSortModel = { direction: 'desc', field: 'revenue_ex_tax' };
  public sourceSegments        : Array<DonutSegmentModel> = [];
  public topCustomers          : Array<IntelligenceTopCustomerModel> = [];
  public topCustomerTotals = { gross_profit_ex_tax: 0, invoice_count: 0, revenue_ex_tax: 0 };
  public visitsValue           : string = '';

  public customerTypes = [
    { key: 'new', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.NEW_CUSTOMERS') },
    { key: 'returning', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.RETURNING') }
  ];

  public jobTypes = [
    { key: 'services', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.SERVICES') },
    { key: 'repairs', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.REPAIRS') },
    { key: 'other', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER') }
  ];

  public newVsReturningSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'new_customers', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.NEW_CUSTOMERS') },
    { color: CHART_COLORS[ 2 ], key: 'returning_customers_invoiced', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.RETURNING') }
  ];

  private loadGeneration : number = 0;
  private subscription   : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  averageInvoice ( customer : IntelligenceTopCustomerModel ) : number {
    return customer.invoice_count
      ? customer.revenue_ex_tax / customer.invoice_count
      : 0;
  }

  clearFilters () : void {
    this.filters = { customer: '', customer_type: '', job_type: '', source: '', suburb: '' };

    this.load();
  }

  hasActiveFilters () : boolean {
    return Object.keys(this.filters).some(key => !!this.filters[ key ]);
  }

  formatMoney ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  isInvoiceSourcesEmpty () : boolean {
    return !this.invoiceSourceSegments.some(segment => segment.value > 0);
  }

  isJobMixEmpty () : boolean {
    return !this.jobMixSegments.some(segment => segment.value > 0);
  }

  isNewVsReturningEmpty () : boolean {
    return !this.newVsReturningPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isSourcesEmpty () : boolean {
    return !this.sourceSegments.some(segment => segment.value > 0);
  }

  isTopCustomersEmpty () : boolean {
    return !this.topCustomers.length;
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getCustomers(this.filters)
    .then(( data : IntelligenceCustomersModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      // Guarded so a frontend that reaches an environment before the backend does
      // keeps its last known lists rather than blanking every dropdown.
      if ( data.filter_options ) {
        this.filterOptions = {
          customers: data.filter_options.customers || [],
          sources  : data.filter_options.sources || [],
          suburbs  : data.filter_options.suburbs || []
        };

        if ( this.dropFiltersMissingFromOptions() ) {
          this.load();

          return;
        }
      }

      this.newVsReturningPoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          new_customers               : day.new_customers,
          returning_customers_invoiced: day.returning_customers_invoiced
        }
      }));

      this.retentionValue = this.intelligenceFormatService.percent(data.retention_rate_pct);
      this.visitsValue = ( data.avg_visits_per_year || 0 ).toFixed(1);

      this.sourceSegments = this.buildSourceSegments(data.sources || {}, true, this.filterOptions.sources);
      this.invoiceSourceSegments = this.buildSourceSegments(data.invoice_sources || {}, true, this.filterOptions.sources);

      const mix = data.job_mix || { other: 0, repairs: 0, services: 0 };

      this.jobMixSegments = [
        { color: CHART_COLORS[ 1 ], drillable: true, key: 'services', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.SERVICES'), value: mix.services },
        { color: CHART_COLORS[ 2 ], drillable: true, key: 'repairs', label: this.translateService.instant('INTELLIGENCE_SPACE.CUSTOMERS.REPAIRS'), value: mix.repairs },
        { color: CHART_COLORS[ 0 ], drillable: true, key: 'other', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'), value: mix.other }
      ];

      this.topCustomers = this.intelligenceFormatService.sortRows(data.top_customers || [], this.sort.field, this.sort.direction);
      this.topCustomerTotals = this.topCustomers.reduce(( totals, customer ) => ({
        gross_profit_ex_tax: totals.gross_profit_ex_tax + customer.gross_profit_ex_tax,
        invoice_count      : totals.invoice_count + customer.invoice_count,
        revenue_ex_tax     : totals.revenue_ex_tax + customer.revenue_ex_tax
      }), { gross_profit_ex_tax: 0, invoice_count: 0, revenue_ex_tax: 0 });
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  dayLabel () : string {
    return this.intelligenceService.previousRange
      ? this.intelligenceFormatService.date(this.intelligenceService.range.start_date)
      : '';
  }

  jobTypeLabel () : string {
    const match = this.jobTypes.find(type => type.key === this.filters.job_type);

    return match
      ? match.label
      : '';
  }

  restoreDay () : void {
    this.intelligenceService.restoreRange();
  }

  selectDay ( point : ChartPointModel ) : void {
    this.intelligenceService.selectDay(point.key);
  }

  setFilter ( field : keyof IntelligenceCustomerFiltersModel, value : string ) : void {
    this.filters[ field ] = value;

    this.load();
  }

  toggleFilter ( field : keyof IntelligenceCustomerFiltersModel, value : string ) : void {
    this.setFilter(field, this.filters[ field ] === value
      ? ''
      : value);
  }

  topCustomersCsvRows () : Array<any> {
    return this.topCustomers.map(customer => ({
      customer           : customer.display_name,
      invoice_count      : customer.invoice_count,
      revenue_ex_tax     : customer.revenue_ex_tax,
      avg_per_invoice    : Math.round(this.averageInvoice(customer) * 100) / 100,
      gross_profit_ex_tax: customer.gross_profit_ex_tax
    }));
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.topCustomers = this.intelligenceFormatService.sortRows(this.topCustomers, this.sort.field, this.sort.direction);
  }

  // Customer type and job type are fixed lists, so only the period-scoped filters can
  // fall away. Reports whether anything was dropped: the payload that carried these
  // options was narrowed by a filter this period has no option for, so the caller has
  // to ask again before showing it.
  private dropFiltersMissingFromOptions () : boolean {
    const kept = {
      customer: this.intelligenceFormatService.keepIfOffered(this.filters.customer, this.filterOptions.customers),
      source  : this.intelligenceFormatService.keepIfOffered(this.filters.source, this.filterOptions.sources),
      suburb  : this.intelligenceFormatService.keepIfOffered(this.filters.suburb, this.filterOptions.suburbs)
    };
    const dropped = kept.customer !== this.filters.customer
      || kept.source !== this.filters.source
      || kept.suburb !== this.filters.suburb;

    this.filters = { ...this.filters, ...kept };

    return dropped;
  }

  private buildSourceSegments ( counts : { [ label : string ] : number }, drillable = false, options : Array<string> = null ) : Array<DonutSegmentModel> {
    const entries = Object.keys(counts)
      .map(label => ({ count: counts[ label ] || 0, label }))
      .sort(( first, second ) => second.count - first.count);

    const kept = entries.slice(0, DONUT_COLORS.length);
    const remainder = entries.slice(DONUT_COLORS.length).reduce(( sum, entry ) => sum + entry.count, 0);

    const segments : Array<DonutSegmentModel> = kept.map(( entry, index ) => ({
      color: donutColor(entry.label, options, index),
      drillable,
      key  : entry.label,
      label: entry.label,
      value: entry.count
    }));

    if ( remainder > 0 ) {
      segments.push({
        color : DONUT_OTHER_COLOR,
        label : this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
        others: entries.slice(DONUT_COLORS.length).map(( entry, index ) => ({
          color: donutColor(entry.label, options, DONUT_COLORS.length + index),
          drillable,
          key  : entry.label,
          label: entry.label,
          value: entry.count
        })),
        value : remainder
      });
    }

    return segments;
  }
}
